// AI controller — pure-pursuit lookahead on the racing line, a
// curvature-derived target speed, plus traffic awareness so cars
// don't run into each other. Returns the same {throttle, brake,
// steerTarget} shape as the keyboard controller; physics never knows
// the difference.
//
// Algorithm per tick:
//   1. Find the car's nearest point on the dense racing line.
//   2. Walk forward by a speed-scaled lookahead distance to pick a
//      target point.
//   3. Steer toward the target — the angle between the car's heading
//      and the bearing to the target, mapped to [-1, 1].
//   4. Look further ahead by the car's straight-line braking distance,
//      find the slowest cornering-speed-limit in that range. Apply
//      the driver's cornering safety factor (aggression-scaled).
//   5. Check for a car ahead within braking distance + following
//      buffer; if there is one and we're closing, lower the target
//      to match their speed.
//   6. P-controlled throttle/brake from the delta to current speed.
//
// Behavior knobs at the top of the file. Defaults are tuned for the
// DEFAULT_CAR_SPEC in world.js — a high-grip open-wheeler. Per-driver
// scaling lives in the `profile` block below — `aggression` and
// `skill` come from car.driverProfile.
//
// Falls back to {0,0,0} when there's no racing line — the car sits.
// Author a line via the editor's "Line" tab to make the AI drive.

import { nearestCarAhead, nearestCarAlongside } from './traffic.js';
import { edgeProximity } from './track-queries.js';
import { DEFAULT_DRIVER_PROFILE, activeLineFor, isOnPitRoute } from './world.js';
import { forwardArcGap } from '../../shared/racing-line.js';

// Lookahead distance: base + k × speed, then scaled down by local
// curvature.
//   At rest (grid spawn) ~5 m — enough to commit to a direction
//   without dithering, short enough that the AI pulls onto the line
//   smoothly from any starting offset.
//   At 50 m/s (~180 km/h) ~35 m on a straight — about a car-length
//   per 10 km/h, the rule of thumb human drivers self-report.
//   In a corner, scaled down. At a hairpin (R=10 m, κ=0.1) the same
//   35 m lookahead would aim across the inside of the corner; the
//   AI would chord-cut the apex and exit too tight, exceeding line
//   curvature → spinout. Shorter lookahead in tight curvature pulls
//   the target toward the actual line geometry.
const LOOKAHEAD_BASE_M  = 5;
const LOOKAHEAD_PER_MS  = 0.6;
const LOOKAHEAD_K_SCALE = 30;  // factor = 1 / (1 + κ × this)

// Trajectory back-prop scan range. Should cover the longest braking
// distance the AI might need plus the corner that's slowing it. At
// 60 m/s with brakeAccel ~25 m/s², a full brake to ~12 m/s needs
// ~77 m — roughly the depth of a tight pit entry's worst curve from
// its start. 120 m gives the AI margin to see + plan that brake
// event before the curve becomes a surprise. Wider scan doesn't
// make the AI brake earlier on race corners (those are well within
// 80 m); it just covers the longer braking windows that pit entries
// demand.
const BACKPROP_SCAN_RANGE_M = 120;

// Steering scale: angle to lookahead target → steerTarget. 0.4 rad
// (~23°) maps to full lock. Tighter values (smaller divisor) make
// the AI commit to corners more aggressively but oscillate on
// straights; looser values (larger) round off late entry.
const STEER_REF_RAD = 0.4;

// Throttle/brake P controller. Separate gains because the dynamics
// are different: throttle authority is continuous (any value works
// for cruise), while braking needs to track a tightening trajectory
// (the back-prop cap drops fast as the AI nears a corner). Brake
// gain is therefore higher.
//   CRUISE_THROTTLE — steady-state throttle floor when at the cap.
//                     The P-controller climbs above this for any
//                     under-target error, but we don't want it to
//                     fall below — even a 0 error needs *some*
//                     throttle to balance drag at speed.
//   THROTTLE_GAIN  — m/s of error per unit input on the gas side.
//                    1.5 collapses the lift band to ~0.4 m/s, so
//                    cars stay pinned to full throttle until they're
//                    essentially at the cap. Earlier values (0.2)
//                    spread the ramp over 3 m/s, which read as
//                    "lifting on the straight" — the AI was below
//                    the cap but already at half throttle for a
//                    visible chunk of the straight. With the tighter
//                    band the AI commits to throttle to the brake
//                    point and the brake event has to do more work.
//   BRAKE_GAIN     — same shape, but stiffer. 0.5 → 2 m/s over
//                    target = full brake. With back-prop the AI is
//                    tracking a moving target, so it needs faster
//                    ramp-up to hold the cap as gap closes.
const CRUISE_THROTTLE = 0.4;
const THROTTLE_GAIN   = 1.5;
const BRAKE_GAIN      = 0.5;

// Buffer on the brake-accel used in back-prop. Real brakes deliver
// brakeAccel; the AI plans for 90% of that. The 10% margin covers
// the P-controller's ramp-up delay (the brake can't go from 0 to 1
// instantaneously). Without the buffer the AI plans the brake-point
// optimally but actually applies brake a hair late, missing the
// corner speed by a few m/s. With the buffer, AI starts braking
// slightly early — sub-1m of lap-time cost, way better corner
// reliability.
const PLANNING_BRAKE_FRACTION = 0.9;

// Floor for |κ| to avoid divide-by-zero on perfect straights.
// 1e-4 corresponds to R = 10 km — "infinite" for our purposes.
const K_MIN = 1e-4;
const G     = 9.81;

// Smoothing constants for the controller outputs. Both are 1-pole
// IIR factors α: new = α × raw + (1−α) × prev. Smaller α = heavier
// smoothing (more lag, smoother bars).
//
// Source of the chaos: the 120 m back-prop scan finds the minimum
// future v_corner across the lookahead window. As the car advances
// 1 m / tick (60 m/s × 1/60 s), tight corners discretely enter and
// leave that window, so minCornerSpeed steps frame-to-frame. The
// P-controller then swings throttle/brake hard. Real driver inputs
// are continuous; smoothing the SPEED TARGET (not the outputs)
// preserves brake-event sharpness when the target genuinely jumps,
// while erasing the per-tick discretization noise.
//
// 0.5 corresponds to a ~33 ms time constant at 60 Hz — well below
// human reaction time, no lap-time impact, but enough to kill the
// visual jitter on the throttle/brake bars.
//
// Steering gets a lighter filter (0.7, ~14 ms) — pure-pursuit's
// target jitter is small to begin with, and we don't want to lag
// corner turn-in. Just enough to round off single-tick spikes.
const SPEED_TARGET_SMOOTH_A = 0.5;
const STEER_SMOOTH_A        = 0.7;

// Driver-profile-scaled tunings. Each helper takes profile.aggression
// (0..1) and returns the scaled value. Personality plumbing lives
// here — adding a new behavior knob means adding a function here +
// using it in the controller body. Keeps the controller readable
// and the design space explicit.
//
//   corneringSafety — fraction of theoretical max corner speed the
//     AI targets. v_target = sqrt(µg/κ) × this. Aggressive drivers
//     pull TOWARD the grip limit (less margin → faster lap, more
//     spinout risk); cautious ones leave headroom (slower but
//     stable). 0.80 (cautious) → 0.95 (aggressive).
//   followingBuffer — extra arc-length distance the AI keeps behind
//     the car ahead, on top of its straight-line braking distance.
//     2.5 × car length (cautious) → 0.5 × car length (aggressive).
//     Aggressive drivers tail close — looks like racing, sets up
//     overtake attempts when those land.
//   overtakeClosingThreshold — m/s of closing rate required before
//     the AI commits to a swerve-around pass on a straight. Cautious
//     drivers wait for a big gap (5 m/s); aggressive drivers commit
//     on a sliver (1 m/s).
function corneringSafety(aggression) {
  return 0.80 + aggression * 0.15;
}
function followingBuffer(aggression, carLength) {
  return carLength * (2.5 - aggression * 2.0);
}
// m/s of closing rate required before the AI commits to a swerve-
// around pass on a low-curvature section. Tuned for racing feel:
// even a 1 sec/lap faster car only closes at ~0.5 m/s on average,
// so the threshold has to be small or AI cars never attempt passes
// on similar-pace rivals.
//   aggression=0   → 2 m/s   (cautious — only obvious mismatches)
//   aggression=0.5 → 1.1 m/s (default — small but real deltas)
//   aggression=1   → 0.2 m/s (aggressive — any forward closing)
function overtakeClosingThreshold(aggression) {
  return 2 - aggression * 1.8;
}

// Drive-mode scalars — pace dial controlled by the team via the
// race-control panel. Multiplies into the existing corneringSafety
// + throttle/cruise constants so the same controller code drives
// every mode; switching modes is purely a multiplier swap.
//
//   safetyMul         — scales the final corneringSafety output
//                       (after aggression + tire-life). >1 = bolder
//                       (smaller reserve), <1 = more conservative.
//   throttleGainMul   — scales THROTTLE_GAIN. Higher = sharper
//                       transition from cruise to full throttle (the
//                       "committed throttle" feel); lower = wider
//                       lift band, AI eases up earlier on approach.
//   cruiseThrottleMul — scales CRUISE_THROTTLE. Lower = the AI
//                       maintains less power at the cap, so drag
//                       bleeds speed off faster — visible on long
//                       sweepers and pre-brake-zone coasts.
//
// Tuning targets:
//   push     — ~1-2 s faster lap, ~10 % more wear, ~5 % more fuel
//   conserve — ~5-6 s slower lap, ~30 % less wear, ~20 % less fuel
// Real numbers depend on track + compound; these are starting points.
export const DRIVE_MODES = {
  normal:   { safetyMul: 1.00, throttleGainMul: 1.0, cruiseThrottleMul: 1.0 },
  push:     { safetyMul: 1.05, throttleGainMul: 1.3, cruiseThrottleMul: 1.0 },
  conserve: { safetyMul: 0.92, throttleGainMul: 0.3, cruiseThrottleMul: 0.7 },
};

function getDriveMode(name) {
  return DRIVE_MODES[name] || DRIVE_MODES.normal;
}

// Cool-down lap pace cap. Multiplied onto minCornerSpeed for cars
// flagged _raceFinished by the race-state phase machine, so the
// post-checkered drive in is a parade lap rather than a hot lap.
// 0.5 ≈ "half pace, but still rolling" — slow enough to read as a
// cool-down without the car looking like it stalled.
const COOLDOWN_PACE_MUL = 0.7;

// Tire-aware reserve. As tires wear the AI widens its corner-speed
// margin so a small slip doesn't put a wheel off — a thoughtful
// driver respects what's left. Multiplied INTO corneringSafety so
// it stacks with aggression: an aggressive driver on worn tires is
// still faster than a cautious driver on worn tires, but BOTH back
// off relative to fresh.
//   tireLife=1.0 (fresh): 1.00  (no reduction)
//   tireLife=0.5 (worn):  0.925 (~7 % off the corner-speed cap)
//   tireLife=0.0 (bald):  0.85  (15 % off)
// Note: this stacks on top of the AI's grip estimate ALSO dropping
// (effectiveSpec.grip falls with tireLife) — the combined effect is
// noticeable lap-time loss as tires go off, with the car staying on
// track instead of taking corners at the same line a fresh-tire AI
// would aim for.
function tireReserveFactor(tireLife) {
  return 0.85 + 0.15 * tireLife;
}

// Pit-zone grip safety floor. Inside the speed zone, the AI targets
// pitLimit unconditionally — except when the grip-limited v_corner
// (sqrt(µg/κ)·safety) drops below this fraction of pitLimit, at
// which point the lane is too tight to take at the limit and we
// fall back to the grip limit. 0.6 = "respect grip when the corner
// would need more than ~2.8× the lateral g of a flat pit-limit
// cruise". Mild authoring noise stays well above; a hand-authored
// hairpin in pit drops below.
const PIT_ZONE_GRIP_FLOOR = 0.6;

// Self-preservation — when the car has drifted close enough to the
// track edge that the next moment of no-grip might put a wheel off,
// scale down the cornering-speed cap and bias the lookahead inward
// (toward centerline). Triggers only when the car's velocity has an
// outward component — an apex-grazing car running tight to the inside
// curb has tangential velocity, so it doesn't false-trigger; a corner-
// exit car drifting wide HAS outward velocity and is exactly the case
// we want to catch.
//
//   edgePanicMargin(aggression) — meters past the car's body half-width
//     before panic engages. Aggressive drivers tolerate less margin
//     (run kerbs); cautious drivers correct earlier.
//   EDGE_PANIC_SLOW_MAX — fraction of corner speed shaved at full panic
//     (distToEdge → carHalf, full outward velocity). 0.4 = up to 40 %
//     reduction. Doesn't compound with the back-prop cap; takes the min.
//   EDGE_PANIC_BIAS_M  — meters of inward lookahead bias at full panic.
//     Pure-pursuit then steers toward the biased target, peeling the
//     car back onto the track surface.
function edgePanicMargin(aggression) {
  return 1.0 - aggression * 0.7;   // 1.0 (cautious) → 0.3 (aggressive)
}
const EDGE_PANIC_SLOW_MAX = 0.4;
const EDGE_PANIC_BIAS_M   = 1.5;

// Neighbor courtesy — in the alongside-hold branch, if the rival has
// less edge margin than we do (and is genuinely close to the edge),
// fade our held lateral offset toward the racing line so we're not
// shoving them off. Without this, two cars trade paint side-by-side
// and whichever was wider gets pushed over the curb. The trigger is
// "rival's distance from the edge minus their carHalf" — i.e. their
// own outer wheel's clearance.
//
//   NEIGHBOR_YIELD_TRIGGER_M — meters of rival outer-wheel clearance
//     below which we start yielding.
//   NEIGHBOR_YIELD_FACTOR    — factor applied to held offset (0..1).
//     0.5 = give them half a meter back. 0 would fully bail and yank
//     the AI back onto the line, reading as a phantom turn-in.
const NEIGHBOR_YIELD_TRIGGER_M = 0.8;
const NEIGHBOR_YIELD_FACTOR    = 0.5;

// Slow-car overtake trigger — fires regardless of aggression.
// If the car ahead is moving at less than this fraction of the AI's
// own target corner speed, treat them as a stationary obstacle and
// steer around (no closing-rate check needed; their speed delta to
// us is large by definition).
const OVERTAKE_SLOW_RATIO = 0.5;

// Curvature at the AI's current line position below which a section
// is "straight enough" for a racing-pass attempt. 0.02 (1/m,
// R = 50 m) — covers genuine straights AND fast sweepers, where
// real overtakes happen. Tighter than this is a slow corner where
// swerving costs more lap time than it gains.
const STRAIGHT_K_THRESHOLD = 0.02;

// Lateral safety pad added to AI's own car width when computing the
// side-step distance for an overtake. 1.5 m gives ~half a car width
// of clear air past the slow car.
const OVERTAKE_LATERAL_PAD_M = 1.5;

// Distance over which the overtake lateral offset tapers in. At
// gap = OVERTAKE_TAPER_RANGE_M the offset is 0; at gap = 0 it is
// full. Smooths the approach so the AI doesn't snap-step sideways.
const OVERTAKE_TAPER_RANGE_M = 30;

// Deadband on slow car's lateral offset before we treat them as
// "drifted to a side". Within ±0.3 m of the line they're "centered"
// and we pick a default side (left) to pass on.
const OVERTAKE_LAT_DEADBAND_M = 0.3;

// Arc-distance window (m) over which a pit-bound car peels laterally
// toward its assigned box on approach, and back to the lane center
// on exit. 20 m feels right at pit-limit speed (~22 m/s) — the lateral
// shift is gentle (~0.2 m/m), the car doesn't snap sideways at the
// last second, and there's enough room left between adjacent boxes'
// approach windows that two cars peeling toward neighbouring boxes
// don't overlap their bias zones.
const BOX_APPROACH_M = 20;

// Caution gap-control. Each follower targets a fixed arc-distance
// behind its snapshot predecessor (`_cautionPredIdx` from race-state).
// Target speed is a P-loop on the gap error, clamped between a floor
// (so trailing cars never crawl) and a ceiling (so they never blow
// past the bunch). Replaces the prior fixed PACE / CHASE cap split,
// which couldn't actually MAINTAIN a gap — it just bounded speeds
// and let the existing follow-match clip cars to predecessor pace
// once they were close enough, leaving stretched gaps stretched.
//
//   TARGET_GAP_M  — desired arc distance to predecessor. ~2 car-
//     lengths reads as "in formation" without overlap risk on small
//     lateral drifts during corner peel-back.
//   GAP_GAIN      — m/s of speed adjustment per meter of gap error.
//     0.5 closes a 10 m-too-far error in ~2 sec given the headroom
//     between predSpeed and the ceiling.
//   GAP_FLOOR_MS  — slowest the trailing car will run when too close,
//     prevents the P-controller from chattering near zero (which
//     visually reads as a stopped car under low-speed P feedback).
//   GAP_CEILING_MS — fastest the trailing car will run when far
//     behind. Capped so a car a half-lap back doesn't barrel into
//     the bunch at racing speed. 34 m/s ≈ 76 mph: ~30 mph faster
//     than the leader's pace cap (20 m/s ≈ 45 mph), enough to close
//     a meaningful gap in a lap or two without making the catch-up
//     run look like racing pace.
const CAUTION_TARGET_GAP_M   = 10;
const CAUTION_GAP_GAIN       = 0.5;
const CAUTION_GAP_FLOOR_MS   = 10;
const CAUTION_GAP_CEILING_MS = 34;

// Forward distance past the entry-ribbon → pit-lane seam to aim the
// lookahead at while traversing the entry ribbon. Keeps the target on
// the pit lane proper (not on the ribbon itself), so pure-pursuit pulls
// the car laterally toward the lane instead of meekly following the
// ribbon's near-straight first half. 8 m gives a dense-segment-or-two
// of forward bias — enough that the angle-to-target reflects the lane's
// direction, not the seam-point's tangent. Smaller (≈3 m) makes the
// pull weaker on long ribbons; larger over-steers tight ribbons.
const ENTRY_LOOKAHEAD_BIAS_M = 8;

export function createAIController() {
  return function aiInputs(self, world, _dt) {
    // Box-stop short-circuit. While serving a pit stop the car holds
    // the brake at zero throttle; the phase machine in main.js times
    // the service window and flips back to 'pitting' when done.
    if (self.pitState?.phase === 'boxStopped') {
      return { throttle: 0, brake: 1, steerTarget: 0 };
    }
    // Disabled-car limp-off. Blown tires or out of fuel = the car
    // can't race anymore but the driver still has limited authority
    // (see BLOWN_INPUT_AUTHORITY in condition.js, and an empty tank
    // leaves steering + brakes intact). Aim for the runoff so the
    // wreck gets out of the racing line. Steer sign is fixed by
    // track direction so the bias is deterministic per track:
    // CCW peels right (toward the outside of an oval-style track),
    // CW peels left. Reduced steering authority on blown tires
    // produces a slow drift that the car can sustain without
    // spinning. Once the car has bled off speed, lock it down — full
    // brake + zero steer — so DNF wrecks come to a definite rest
    // instead of slow-circling indefinitely on residual angular
    // velocity from the constant peel-off steer command.
    const cond = self.condition;
    if (cond && (cond.blownTires || cond.fuel <= 0)) {
      const speed = Math.hypot(self.vel.x, self.vel.y);
      if (speed < 2) {
        // Settled — pin everything. brake=1 holds against any tiny
        // residual force; steer=0 stops the car from torquing itself
        // around in a slow loop.
        return { throttle: 0, brake: 1, steerTarget: 0 };
      }
      const dir = world.track?.direction === 'cw' ? -1 : 1;
      // Brake firms up as speed bleeds off — light at racing speeds
      // (slow peel-off, dignified), then progressively heavier so the
      // car actually reaches the locked-rest state above instead of
      // taking forever to crawl below 2 m/s.
      const brakeNorm = speed > 30 ? 0.25 : (speed > 10 ? 0.5 : 0.85);
      return { throttle: 0, brake: brakeNorm, steerTarget: 0.6 * dir };
    }
    // Cool-down auto-pit. Once a car has taken their checkered
    // (raceState marked them via _raceFinished), request a pit stop
    // so the existing pit AI routes them into their assigned box on
    // the way down the cool-down lap. The actual speed slowdown is
    // applied below — the corner-speed cap gets multiplied by
    // COOLDOWN_PACE_MUL after the back-prop has run, so it composes
    // with the pit-zone limit cleanly. No pit route → no auto-pit;
    // the car coasts a cool-down lap and brakes to a stop at S/F.
    if (self._raceFinished && world.geometry.pitRoute && self.pitState?.phase === 'racing') {
      self.pitState.requested = true;
    }
    // Line source depends on pit phase. Racing line (closed loop) by
    // default; pit route (open path: entry → pit lane → exit) while
    // pitting. Same shape — the rest of the controller is agnostic.
    const line = activeLineFor(self, world.geometry);
    if (!line || !line.dense || line.dense.length < 4) {
      return { throttle: 0, brake: 0, steerTarget: 0 };
    }
    const dense     = line.dense;
    const curvature = line.curvature;
    const N         = dense.length;
    const isClosed  = line.closed !== false;
    const isPitting = isOnPitRoute(self);

    // Racing-line nearest-point read from per-car cache (refreshed in
    // main.js's pre-controller pass via updateLineCache). Cold cache
    // (lineSegIdx === -1) means no usable line for this car this tick.
    const lineSegIdx = self.derived.lineSegIdx;
    if (lineSegIdx < 0) return { throttle: 0, brake: 0, steerTarget: 0 };
    const linePointX = self.derived.linePointX;
    const linePointY = self.derived.linePointY;

    const speed   = Math.hypot(self.vel.x, self.vel.y);
    const profile = self.driverProfile || DEFAULT_DRIVER_PROFILE;

    // ---- target lookahead point ----
    // Lookahead scales DOWN with local line curvature. A long
    // lookahead aimed across a tight corner reads as a chord — pure
    // pursuit then steers across the inside, undercutting the apex
    // and exceeding line curvature. Shortening the lookahead in
    // tight curvature pulls the target back to where the line
    // actually goes.
    const localK         = Math.abs(curvature[lineSegIdx]);
    const baseLookahead  = LOOKAHEAD_BASE_M + LOOKAHEAD_PER_MS * speed;
    let   lookaheadM     = baseLookahead / (1 + localK * LOOKAHEAD_K_SCALE);

    // Entry-ribbon lookahead bias. A pit-entry ribbon is a smooth bezier
    // from the trackline pit-marker to the first authored pit waypoint —
    // by construction its first ~half is nearly aligned with the racing
    // direction (the trackline tangent constrains it). Plain pure-pursuit
    // on this ribbon picks a target that's "mostly straight ahead" with
    // a small lateral component, so the AI follows the ribbon's curve
    // gently. On tracks where the lateral offset is big and the racing
    // line itself curves into a corner past the pit-entry (mid-ohio:
    // ~14 m lateral over a 60 m ribbon, then a corner), "gently" isn't
    // enough — the car drifts forward into the next corner's wall before
    // it ever reaches the pit-lane proper.
    //
    // Overriding the lookahead to a target PAST the ribbon (on the pit
    // lane proper, ENTRY_LOOKAHEAD_BIAS_M past the seam) puts the
    // pure-pursuit angle where the lane actually is, not where the
    // ribbon's near-straight start happens to be. The car then steers
    // aggressively toward the pit lane through the ribbon. Same trick
    // the box-approach override uses, generalized to entry. Geometry-
    // agnostic — works for any ribbon shape.
    if (isPitting && line.entryEndArc != null
        && self.derived.lineArc < line.entryEndArc) {
      const distToSeam   = line.entryEndArc - self.derived.lineArc;
      const biasedLookahead = distToSeam + ENTRY_LOOKAHEAD_BIAS_M;
      if (biasedLookahead > lookaheadM) lookaheadM = biasedLookahead;
    }

    const targetIdx      = walkForwardArc(dense, lineSegIdx, lookaheadM, isClosed);
    let targetX = dense[targetIdx].x;
    let targetY = dense[targetIdx].y;

    // ---- speed target — trajectory back-propagation ----
    // For each future point at arc distance d ahead with cornering
    // speed limit v_corner, the maximum speed the AI can be at NOW
    // and still slow to v_corner by the time it arrives is
    //
    //   v_now_max = √(v_corner² + 2 × brakeAccel × d)
    //
    // Take the min across all scan points. Result: AI runs at full
    // speed up to the optimal brake point, brakes exactly hard
    // enough to make the corner, then accelerates the moment the
    // corner falls out of the back-prop. Fixes "AI brakes way too
    // early" + "AI accelerates too late on exit" in one pass — both
    // were artifacts of the previous lookahead-window min scan,
    // which targeted the SLOWEST FUTURE corner regardless of distance.
    // Plan against effective spec, not spec — tires wear, fuel burns,
    // damage scales the engine and brakes. If the AI plans with the
    // dry-tank-fresh-tire numbers but physics runs on the degraded
    // ones, the AI misjudges grip and brake distance and slides off.
    // effectiveSpec is mutated in place each tick by updateCondition
    // before physics runs, so reading it here gives the AI the same
    // numbers physics will use this tick.
    const eff             = self.effectiveSpec || self.spec;
    const grip            = eff.grip;
    const brakeAccelReal  = eff.brakeForce / eff.mass;
    const brakeAccelPlan  = brakeAccelReal * PLANNING_BRAKE_FRACTION;
    const brakeDist       = (speed * speed) / (2 * brakeAccelReal);
    // Cornering reserve. corneringSafety encodes the driver's
    // personality (aggression → smaller margin); tireReserveFactor
    // encodes tire awareness (worn rubber → bigger margin); the
    // drive mode scales the result up (push) or down (conserve).
    // All three combine multiplicatively so push on fresh tires
    // beats normal, conserve on worn beats normal-on-worn for
    // longevity, etc.
    const mode            = getDriveMode(self.driveMode);
    const tireLife        = self.condition?.tireLife ?? 1;
    const safetyMul       = corneringSafety(profile.aggression)
                          * tireReserveFactor(tireLife)
                          * mode.safetyMul;

    // Pit-lane speed-zone gating — when isPitting, scanned segments
    // whose arc lies between speedZone.startArc and endArc are TARGETED
    // at pl.speedLimit. The back-prop's "minimum upcoming v_corner"
    // then naturally schedules the brake event at the start of the
    // zone (cars don't slow in the entry ribbon) and releases it at
    // the end (cars accelerate through the exit ribbon). Outside the
    // pit phase, this whole block is inert.
    //
    // Why TARGET and not just CAP: dense polylines for authored pit
    // lanes inevitably carry minor curvature (subtle bends, junction
    // tangent kinks at the entry- / exit-ribbon seams). The plain
    // grip-limited v_corner can dip below pitLimit on those segments,
    // and a min(v_corner, pitLimit) would brake the AI to that
    // dipped value — cars under-drive the limiter through long pit
    // lanes for no good reason. Pit lanes are designed for the
    // limit; AI drivers trust that and hold the limit.
    //
    // PIT_ZONE_GRIP_FLOOR is the safety net: if the grip-limited
    // v_corner drops below this fraction of pitLimit, the lane is
    // genuinely too tight to take at the limit (R << pitLimit²/g).
    // We respect the grip limit there to avoid an immediate spin.
    // 0.6 ≈ "the corner needs more than 2.8x the lateral g of a flat
    // pit-limit cruise"; mild authoring noise stays well above this,
    // a hand-authored hairpin in pit doesn't.
    const speedZone = isPitting ? world.geometry.pitSpeedZone : null;
    const pitLimit  = isPitting ? (world.track.pitLane?.speedLimit ?? Infinity) : Infinity;
    const pitGripFloor = pitLimit * PIT_ZONE_GRIP_FLOOR;

    // Box-stop arc — when pitting toward an unserviced assigned box,
    // we treat that arc as a v_corner=0 event in the back-prop loop.
    // The trajectory math then schedules a brake to zero on arrival,
    // so the car decelerates BEFORE the box and lands inside it
    // instead of overshooting into the next divider. Note this is
    // box.stopArc (= centerArc − buffer), not centerArc — the
    // proportional brake controller has lag and needs the buffer to
    // land the car centered on the painted number.
    let boxStopArc = null;
    if (isPitting && self.pitBoxIdx != null && !self.pitState?.serviced) {
      const _b = world.geometry.pitBoxes?.boxes?.[self.pitBoxIdx];
      if (_b) boxStopArc = _b.stopArc;
    }

    // Is the car currently INSIDE the speed zone? When yes, the back-
    // prop terminates at zone end — we don't want post-zone curvature
    // (exit-ribbon merges back to the racing surface, naturally
    // curved) to drag speedCap below pitLimit while the car is still
    // supposed to be holding the limiter. Cars accelerate / brake for
    // post-zone curves AFTER they cross the end gate, not before.
    const carArc = lineSegIdx >= 0 ? line.cumulative[lineSegIdx] : -1;
    const carInZone = !!(speedZone && carArc >= speedZone.startArc && carArc <= speedZone.endArc);

    let speedCap = Infinity;
    let scanned  = 0;
    let i        = lineSegIdx;
    let safety   = N + 2;   // hard wrap guard for closed loops
    while (scanned < BACKPROP_SCAN_RANGE_M && safety-- > 0) {
      const segArc = line.cumulative[i];
      // Stop scanning past zone end while the car is in zone — see
      // carInZone comment above. We do this BEFORE computing v_corner
      // so the speedCap reflects only zone segments.
      if (carInZone && speedZone && segArc > speedZone.endArc) break;
      const k        = Math.abs(curvature[i]);
      const v_grip   = Math.sqrt((grip * G) / Math.max(k, K_MIN)) * safetyMul;
      let v_corner   = v_grip;
      // Per-segment pit speed target. The arc-range check uses the
      // segment's start arc — close enough; segments are ~1 m and
      // the zone spans tens of meters.
      if (speedZone && segArc >= speedZone.startArc && segArc <= speedZone.endArc) {
        // Drive AT the limit through the zone, regardless of small
        // curvature. Only fall back to the grip-limited speed when
        // the lane is genuinely too tight (below the safety floor) —
        // a real authored hairpin, not noise.
        v_corner = v_grip < pitGripFloor ? v_grip : pitLimit;
      }
      // Force a stop event at (or past) the assigned box. v=0 here
      // dominates anything else for this segment so the back-prop
      // schedules a clean brake to zero on arrival.
      if (boxStopArc != null && segArc >= boxStopArc) {
        v_corner = 0;
      }
      const v_now_max  = Math.sqrt(v_corner * v_corner + 2 * brakeAccelPlan * scanned);
      if (v_now_max < speedCap) speedCap = v_now_max;
      scanned += line.segLengths[i];
      // Open polyline (pit route) — stop at the last point. There's
      // no segment past it, and segLengths[N-1] is 0, so continuing
      // would just spin. The phase machine hands control back to the
      // racing line before this point matters in practice.
      if (!isClosed && i >= N - 1) break;
      i = isClosed ? (i + 1) % N : i + 1;
    }
    // Pre-commit pit braking. The back-prop above scans the active line
    // — when the car has requested a pit but is still on the racing
    // line, it can't see the speed-zone constraint that lives on the
    // pit route. On tracks with a long entry ribbon and an early speed
    // zone (e.g. mid-ohio: ~60 m ribbon + zone start at pit-lane wp 2,
    // ~140 m total from racing-line entry to zone), the back-prop's
    // 120 m scan won't reach the zone even right at the commit point.
    // Result: AI commits at PIT_COMMIT_TRIGGER_M still at racing speed,
    // can't slow + peel laterally in time, overshoots into the wall.
    //
    // Fix: project the speed-zone-start as if it were a corner ahead
    // on the current line, at distance (gap-to-entry-on-racing-line)
    // + (pit-route arc of zone start). The back-prop kinematic formula
    // self-gates by distance — at half a lap away it's a non-binding
    // ~200 m/s cap; at 50 m away from the zone it's a real brake event.
    // No track-specific tuning; works for any entry ribbon length and
    // any speed-zone placement the editor produces.
    if (self.pitState?.requested && !isPitting
        && world.geometry.pitEntryRacingLineArc != null
        && world.geometry.pitSpeedZone != null
        && world.track.pitLane?.speedLimit != null
        && world.geometry.racingLine?.totalLength) {
      const entryArc       = world.geometry.pitEntryRacingLineArc;
      const racingLineLen  = world.geometry.racingLine.totalLength;
      const carArc         = self.derived.lineArc;
      const distToEntry    = forwardArcGap(carArc, entryArc, racingLineLen);
      const distToZoneStart = distToEntry + world.geometry.pitSpeedZone.startArc;
      const pitLimitV      = world.track.pitLane.speedLimit;
      const v_now_max      = Math.sqrt(pitLimitV * pitLimitV + 2 * brakeAccelPlan * distToZoneStart);
      if (v_now_max < speedCap) speedCap = v_now_max;
    }

    let minCornerSpeed = speedCap;

    // Smooth the speed target to remove back-prop discretization noise
    // (see SPEED_TARGET_SMOOTH_A comment). The smooth state lives on
    // the car so it survives across ticks; first tick after spawn /
    // reset seeds with the raw value so we don't ramp up from zero.
    const prevSmoothV = self.derived.minCornerSpeedSmooth;
    if (prevSmoothV == null) {
      self.derived.minCornerSpeedSmooth = minCornerSpeed;
    } else {
      self.derived.minCornerSpeedSmooth =
        prevSmoothV + SPEED_TARGET_SMOOTH_A * (minCornerSpeed - prevSmoothV);
      minCornerSpeed = self.derived.minCornerSpeedSmooth;
    }

    // ---- traffic awareness ----
    // Two outcomes based on what's ahead:
    //
    //   OVERTAKE — keep target speed AND steer around. Lateral offset
    //     added to the lookahead point so pure-pursuit guides the AI
    //     past. Side: opposite of where the slow car drifted from the
    //     line; LEFT if centered. Offset tapers in over the last
    //     OVERTAKE_TAPER_RANGE_M so the AI eases sideways instead
    //     of snap-stepping.
    //
    //   FOLLOW — match their speed if within braking + buffer range.
    //     Conga-line behavior; AI sits behind without bumping in.
    //
    // Overtake fires in two cases:
    //   SLOW: the car ahead is moving at <50% of my target corner
    //     speed — stopped, lost, or seriously off pace. Pass no
    //     matter what. Independent of aggression.
    //   RACING: I'm catching them on a low-curvature section by
    //     more than the aggression-scaled closing threshold. The
    //     "on a straight" gate keeps the AI from swerving in slow
    //     corners where everyone targets similar speeds. The
    //     aggression scaling is the personality dial — cautious
    //     drivers commit only on big deltas; aggressive drivers
    //     attempt thin margins.
    let lateralOffsetM = 0;       // applied to lookahead before steering
    // Caution detection reads `_cautionRank` — set on every on-track
    // car (leader and followers) by race-state's per-tick stamp pass.
    // Was `_cautionPaceTarget != null` before gap-control landed; that
    // flag is now leader-only, which would have made followers think
    // they weren't under caution.
    const underCaution = self._cautionRank != null;
    // Green-flag restart grace. The instant caution lifts, follower
    // target speeds jump from the gap-control cap to full racing
    // pace; transient P-controller asymmetries trip the racing-pass
    // branch and a tight pack pile-drives itself trying to overtake.
    // Suppress overtake (but keep follow-match) until the grace
    // timestamp expires.
    const inGreenFlagGrace = world.raceState
      ? world.simTime < world.raceState.greenFlagGraceUntil
      : false;
    // Under caution, the bunch coordinates via gap-control (below) —
    // nearestCarAhead's overtake-or-follow logic gets in the way: it
    // swerves followers around the leader (chase > pace would trigger
    // the racing-pass branch) and clips trailing cars to predecessor
    // pace within ~25 m, which prevents catching up from a stretched
    // gap. Skip the whole block under caution.
    const ahead = underCaution ? null : nearestCarAhead(self, world);
    if (ahead) {
      const aheadSpeed = Math.hypot(ahead.car.vel.x, ahead.car.vel.y);
      const isMuchSlower    = !inGreenFlagGrace
        && aheadSpeed < minCornerSpeed * OVERTAKE_SLOW_RATIO;
      const closingDelta    = speed - aheadSpeed;
      const isLowCurvature  = Math.abs(curvature[lineSegIdx]) < STRAIGHT_K_THRESHOLD;
      const isRacingPass    = !inGreenFlagGrace
        && isLowCurvature
        && closingDelta > overtakeClosingThreshold(profile.aggression);
      const wantsOvertake   = isMuchSlower || isRacingPass;

      if (wantsOvertake) {
        // OVERTAKE — compute lateral offset to lookahead. Side:
        // opposite of where slow car drifted from the line; LEFT if
        // centered. Tapers in by gap so the AI eases sideways
        // instead of snap-stepping.
        const aheadSegIdx = ahead.car.derived.lineSegIdx;
        if (aheadSegIdx >= 0) {
          const aheadTng = line.tangents[aheadSegIdx];
          const aheadNx  = -aheadTng.y;
          const aheadNy  =  aheadTng.x;
          const dxL = ahead.car.pos.x - ahead.car.derived.linePointX;
          const dyL = ahead.car.pos.y - ahead.car.derived.linePointY;
          const aheadLat = dxL * aheadNx + dyL * aheadNy;
          const passSign = aheadLat > OVERTAKE_LAT_DEADBAND_M ? -1 : 1;
          const taper = clamp(1 - ahead.gap / OVERTAKE_TAPER_RANGE_M, 0, 1);
          lateralOffsetM = passSign * (self.spec.carWidth + OVERTAKE_LATERAL_PAD_M) * taper;
        }
        // Don't slow to match — keep cornering speed target.
      } else {
        // FOLLOW — match their speed if within range.
        const buffer = followingBuffer(profile.aggression, self.spec.carLength);
        if (ahead.gap < brakeDist + buffer && aheadSpeed < minCornerSpeed) {
          minCornerSpeed = aheadSpeed;
        }
      }
    }

    // Sticky alongside-hold. The arc-length "nearest ahead" check
    // flips to "nothing ahead" the moment the AI's arc passes the
    // slow car's — even though the slow car is still physically
    // next to the AI. Without this hold, lateralOffsetM drops to 0
    // and pure-pursuit yanks the AI back to the line, clipping
    // the just-passed car. While any car is alongside, lock the
    // lookahead offset to the AI's CURRENT lateral position from
    // the line — pure-pursuit then holds position until clear.
    // Pick the active-path edgeProximity reference. Pit-lane cars get
    // projected onto the pit route (the path they're supposed to be
    // on); racing cars onto the racing-surface centerline. Same
    // semantics either way — "how far am I from the edge of the
    // surface I should be on?".
    const epPath = isPitting ? 'pit' : 'racing';

    if (lateralOffsetM === 0 && !underCaution) {
      const alongsideCar = nearestCarAlongside(self, world);
      if (alongsideCar) {
        const myTng = line.tangents[lineSegIdx];
        const myNx  = -myTng.y;
        const myNy  =  myTng.x;
        const dxM = self.pos.x - linePointX;
        const dyM = self.pos.y - linePointY;
        let heldOffset = dxM * myNx + dyM * myNy;
        // Neighbor courtesy. If our rival has less edge margin than we
        // do, fade our held offset toward the line — without it, the
        // car-with-room locks its position and the car-without-room
        // gets shoved off. Self margin guards against the case where
        // we're the one closer to the edge (don't yield to the rival
        // who has all the clearance). Both projections use the same
        // path so the margins are comparable.
        const myEp    = edgeProximity(self.pos,         world.geometry, { path: epPath });
        const theirEp = edgeProximity(alongsideCar.pos, world.geometry, { path: epPath });
        if (myEp && theirEp) {
          const myMargin    = myEp.distToEdge    - self.spec.carWidth         / 2;
          const theirMargin = theirEp.distToEdge - alongsideCar.spec.carWidth / 2;
          if (theirMargin < myMargin && theirMargin < NEIGHBOR_YIELD_TRIGGER_M) {
            heldOffset *= NEIGHBOR_YIELD_FACTOR;
          }
        }
        lateralOffsetM = heldOffset;
      }
    }

    // Caution single-file enforcement. Two cars side-by-side at yellow
    // are a stable equilibrium under the plain pace cap (both match
    // speeds, neither budges). Break the tie by ranking: the lower-
    // ranked car of an alongside pair drops below pace until they're
    // properly behind. Once the car is no longer alongside, the
    // drop-back stops, the chase cap kicks back in, and the existing
    // follow-match clips them to the predecessor's pace. End result:
    // single-file in standings order over a few seconds. The hold
    // above is suppressed under caution (`!underCaution`) so the
    // higher-ranked car doesn't lock its lateral position; both cars
    // drift back to the racing line via plain pure-pursuit.
    //
    // FIXED target (not a multiplier on alongsideSpeed) to avoid
    // catastrophic compounding through a wide formation: a 5-wide
    // group with each rank dropping to 0.7× the next had the tail
    // settle at <2 m/s, which the P-controller renders as cars
    // stopped on track. A flat target gives every alongside car the
    // same falling-back pace; the higher-ranked car (still on chase /
    // pace cap) pulls away and the formation resolves front-to-back.
    // 12 m/s ≈ 27 mph — slow enough to fall behind the 20 m/s leader
    // pace at 8 m/s relative (clears the alongside window in ~1 sec)
    // without dropping into crawl-speed P-controller chatter.
    const CAUTION_DROPBACK_TARGET_MS = 12;
    if (underCaution && self._cautionRank != null) {
      const alongsideCar = nearestCarAlongside(self, world);
      if (alongsideCar
          && alongsideCar._cautionRank != null
          && alongsideCar._cautionRank < self._cautionRank) {
        if (minCornerSpeed > CAUTION_DROPBACK_TARGET_MS) {
          minCornerSpeed = CAUTION_DROPBACK_TARGET_MS;
        }
      }
    }

    // Apply the final lateral offset to the lookahead target. Done
    // once here so overtake + alongside-hold share the same code
    // path. (Box approach uses a direct target override below — it
    // doesn't go through this lateral-offset path because pure-pursuit
    // with a fixed offset peels too gently to actually land in the
    // box at full pit-limit speed.)
    if (lateralOffsetM !== 0) {
      const tIdxTng = line.tangents[targetIdx];
      targetX += (-tIdxTng.y) * lateralOffsetM;
      targetY += ( tIdxTng.x) * lateralOffsetM;
    }

    // Box approach: when pitting toward an unserviced assigned box and
    // within the approach window, override the lookahead with a point
    // FORWARD of the box's stop center along the lane direction at
    // lateralOffset. Aiming at the box center directly makes the car
    // peel hard but stops it angled (heading rotated toward the box).
    // Aiming forward-along-lane at lateralOffset gives pure-pursuit a
    // target whose direction-from-car converges to the lane tangent
    // as the car nears the box, so heading aligns with the lane by
    // the time it stops. Combined with the back-prop's v=0 event,
    // the car arrives slow + aligned and lands centered in the box.
    if (boxStopArc != null) {
      const _box = world.geometry.pitBoxes.boxes[self.pitBoxIdx];
      const arcAhead = boxStopArc - self.derived.lineArc;
      if (arcAhead >= 0 && arcAhead < BOX_APPROACH_M && _box.innerEdge.length >= 2) {
        // Box's lane-direction tangent: chord from back to front of
        // the inner edge, normalized.
        const inner = _box.innerEdge;
        const last = inner.length - 1;
        const tx = inner[last].x - inner[0].x;
        const ty = inner[last].y - inner[0].y;
        const tlen = Math.hypot(tx, ty) || 1;
        const ux = tx / tlen, uy = ty / tlen;
        // Box stop center (4-corner centroid — robust to variable
        // edge-vertex counts on curved lanes).
        const outer = _box.outerEdge;
        const oLast = outer.length - 1;
        const cx = (inner[0].x + inner[last].x + outer[0].x + outer[oLast].x) * 0.25;
        const cy = (inner[0].y + inner[last].y + outer[0].y + outer[oLast].y) * 0.25;
        // Forward bias along the lane tangent — far enough that
        // pure-pursuit's angle-to-lookahead is dominated by the lane
        // direction, not the lateral offset. 3 m balances heading
        // alignment (less rotation = car stops parallel to lane)
        // against still-meaningful lateral pull (so it actually
        // peels into the box). 4 m felt over-aligned and the car
        // carried too much along-lane speed into the brake event.
        const FORWARD_BIAS_M = 3;
        targetX = cx + ux * FORWARD_BIAS_M;
        targetY = cy + uy * FORWARD_BIAS_M;
      }
    }

    // Self-preservation — last gate before steering. The plan above
    // (line lookahead + overtake/hold lateral offset) doesn't know
    // where the surface edge is; if the car is drifting off the
    // surface at full throttle the line plan keeps demanding apex
    // pace. Detect proximity to the edge AND outward velocity, then
    // both shave the corner-speed cap and pull the lookahead inward
    // so pure-pursuit peels the car back onto the surface. The
    // outward-velocity gate is what keeps an apex-grazing pass from
    // false-triggering — at the apex the car's velocity is tangent,
    // not outward.
    //
    // Active path matters: a pit-lane car projected onto the racing-
    // surface centerline reads as "way off track" and triggers panic
    // unconditionally — exactly the bug that made pit cars under-
    // drive the speed limit. Projecting onto the pit route instead
    // gives the AI the right reference frame: "am I drifting off the
    // pit lane?" — which is the correct question for a pit-lane car.
    const ep = edgeProximity(self.pos, world.geometry, { path: epPath });
    if (ep) {
      const carHalf  = self.spec.carWidth / 2;
      const safeDist = carHalf + edgePanicMargin(profile.aggression);
      if (ep.distToEdge < safeDist) {
        const outwardSpeed = -(self.vel.x * ep.inwardX + self.vel.y * ep.inwardY);
        if (outwardSpeed > 0) {
          const closenessP = clamp(1 - ep.distToEdge / safeDist, 0, 1);
          minCornerSpeed *= 1 - EDGE_PANIC_SLOW_MAX * closenessP;
          const biasM = EDGE_PANIC_BIAS_M * closenessP;
          targetX += ep.inwardX * biasM;
          targetY += ep.inwardY * biasM;
        }
      }
    }

    // Full-course caution speed cap. Leaders use the simple pace cap
    // (`_cautionPaceTarget` set by race-state). Followers run a P-
    // controlled gap target keyed off their snapshot predecessor:
    //
    //   target = clamp(predSpeed + GAIN × (gap − TARGET_GAP),
    //                  FLOOR, CEILING)
    //
    // Far behind → target near CEILING (catch up). At target gap →
    // match predSpeed. Too close → slow below predSpeed. The result
    // is a self-stabilizing single-file pack at TARGET_GAP_M spacing
    // that closes stretched gaps without overshoot.
    //
    // Manual drivers ignore the cap — `_cautionRank` is stamped but
    // the keyboard controller doesn't read it. Cars in pit / retired
    // / recovered aren't stamped at all and run their own logic.
    if (underCaution) {
      let cautionCap;
      if (self._cautionPredIdx == null) {
        // Leader (or no predecessor available — same outcome).
        cautionCap = self._cautionPaceTarget ?? CAUTION_GAP_CEILING_MS;
      } else {
        const pred = world.cars[self._cautionPredIdx];
        const predOK = pred
          && pred.derived?.racingLineSegIdx >= 0
          && self.derived?.racingLineSegIdx >= 0
          && world.geometry?.racingLine?.totalLength > 0;
        if (predOK) {
          const totalLen  = world.geometry.racingLine.totalLength;
          const gap       = forwardArcGap(self.derived.racingLineArc,
                                          pred.derived.racingLineArc,
                                          totalLen);
          const predSpeed = Math.hypot(pred.vel.x, pred.vel.y);
          const gapError  = gap - CAUTION_TARGET_GAP_M;
          cautionCap = clamp(
            predSpeed + gapError * CAUTION_GAP_GAIN,
            CAUTION_GAP_FLOOR_MS,
            CAUTION_GAP_CEILING_MS,
          );
        } else {
          // Predecessor data missing — fall through to ceiling so the
          // car at least keeps moving with the field.
          cautionCap = CAUTION_GAP_CEILING_MS;
        }
      }
      if (minCornerSpeed > cautionCap) minCornerSpeed = cautionCap;
    }

    // Cool-down pace cap — applied last so it composes with all the
    // upstream caps (back-prop, traffic, edge panic, pit zone). Once
    // the car has taken its checkered, halve target speed for the
    // drive-in / pit lap. On no-pit-lane tracks, stop the car for
    // good after one cool-down lap by pinning the cap to zero — the
    // P-controller below turns that into full brake. _finishLapAt is
    // the lap they were on at flag drop; they finished crossing S/F
    // to lap _finishLapAt+1 and the parade lap completes when they
    // cross again to _finishLapAt+2.
    if (self._raceFinished) {
      const lap          = self.lapState?.lapNumber ?? 0;
      const finishedLap  = self._finishLapAt ?? 0;
      const noPitRoute   = !world.geometry.pitRoute;
      if (noPitRoute && lap > finishedLap + 1) {
        minCornerSpeed = 0;
      } else {
        minCornerSpeed *= COOLDOWN_PACE_MUL;
      }
    }

    // ---- steering (after any overtake offset) ----
    const dx = targetX - self.pos.x;
    const dy = targetY - self.pos.y;
    const targetAngle = Math.atan2(dy, dx);
    let angleDiff = targetAngle - self.heading;
    // Normalize to [-π, π] so a target slightly across the +x axis
    // doesn't read as "turn 350° the other way".
    while (angleDiff >  Math.PI) angleDiff -= 2 * Math.PI;
    while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
    let steerTarget = clamp(angleDiff / STEER_REF_RAD, -1, 1);

    // Light steering smoothing — see STEER_SMOOTH_A. Just enough to
    // round off per-tick target-point jitter without lagging genuine
    // turn-in. Reads previous tick's commanded steerTarget from
    // self.lastInputs (set by main.js after the controller returns).
    const prevSteer = self.lastInputs?.steerTarget;
    if (prevSteer != null) {
      steerTarget = prevSteer + STEER_SMOOTH_A * (steerTarget - prevSteer);
    }

    // ---- throttle/brake ----
    // Drive-mode tweaks the cruise floor and gain only — brake gain
    // stays constant so emergency brake response doesn't get gentler
    // in conserve (the AI still needs to make corners, even if it's
    // taking it easier). Conserve-mode lift band widens (low gain),
    // cruise floor drops (less power held at cap → drag bleeds speed
    // off the way the user described).
    const cruiseT     = CRUISE_THROTTLE * mode.cruiseThrottleMul;
    const throttleGn  = THROTTLE_GAIN   * mode.throttleGainMul;
    const speedError = minCornerSpeed - speed;
    let throttle, brake;
    if (speedError >= 0) {
      throttle = clamp(cruiseT + speedError * throttleGn, 0, 1);
      brake = 0;
    } else {
      throttle = 0;
      brake = clamp(-speedError * BRAKE_GAIN, 0, 1);
    }

    return { throttle, brake, steerTarget };
  };
}

// Walk forward along a closed dense polyline by `arcLength` meters
// from `startIdx`. Returns the index landed on. Wraps around the
// loop. Safety counter prevents an infinite loop on a degenerate
// (zero-length-segment) polyline.
function walkForwardArc(dense, startIdx, arcLength, closed = true) {
  const N = dense.length;
  let i = startIdx;
  let acc = 0;
  let safety = 2 * N + 4;
  while (acc < arcLength && safety-- > 0) {
    // On an open polyline (pit route) the last point is the end of
    // the road — no wrap, no further walking. The AI will steer
    // straight at the exit and pure-pursuit naturally peters out
    // there, which is what we want; the phase machine flips back to
    // racing in the same tick range.
    if (!closed && i >= N - 1) return N - 1;
    const next = closed ? (i + 1) % N : i + 1;
    const segLen = Math.hypot(dense[next].x - dense[i].x, dense[next].y - dense[i].y);
    if (acc + segLen >= arcLength) return next;
    acc += segLen;
    i = next;
  }
  return i;
}

function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x));
}
